/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import metrics from '@adobe/exc-app/metrics';

const EXPIRE_ON_LOGOUT = 0;
// Theshold for storage is 1MB
const THRESHOLD = 1000000;
let compression: any;

export type WindowStorage = typeof localStorage | typeof sessionStorage;

interface ExpirationDictionary {
  [key: string]: number;
}

export class StorageInstance {
  expirationDict: ExpirationDictionary;
  metrics: any;
  storage: WindowStorage;

  constructor(storage: WindowStorage) {
    const expirationDict = storage.getItem('shellStorageExpiration');
    this.expirationDict = expirationDict ? JSON.parse(expirationDict) : {};
    this.metrics = metrics.create('exc.storage.Storage');
    this.storage = storage;
    setInterval(() => this.trackExpiration(), 60000);
  }

  /**
   * Returns item from storage.
   * @param key Key with which to query storage.
   * @returns Promise which resolves to the value in storage.
   */
  async get(key: string): Promise<any> {
    const stringValue = this.storage.getItem(key);
    if (!stringValue) {
      return null;
    }
    // Split the compressed/json part from the rest.
    let [compressed, json] = stringValue.split(/\|(.+)/);
    if (!json) {
      this.remove(key);
      return null;
    }
    const expirationTime = this.expirationDict[key];
    // If the key has expired, remove it
    if (expirationTime > 0 && expirationTime < Date.now()) {
      this.metrics.event('storage expiration', `Key ${key} expired at ${expirationTime}`, {expirationTime, key});
      this.remove(key);
      return null;
    }
    if (compressed === 'Y') {
      compression = compression || await import('./compression');
      json = compression.decompress(json, key);
    }
    return JSON.parse(json);
  }

  /**
   * Returns item expiration time for the key.
   * @param expirationInMs Expiration time to be set.
   * @returns 0 if it expires at logout, or the expiration time.
   */
  getExpireTime(expirationInMs?: number): number {
    return expirationInMs ? Date.now() + expirationInMs : EXPIRE_ON_LOGOUT;
  }

  /**
   * Stores the updated expirationDict in storage.
   */
  storeExpirationDictionary() {
    try {
      this.storage.setItem('shellStorageExpiration', JSON.stringify(this.expirationDict));
    } catch (domException) {
      this.metrics.error('Failed to set expiration dictionary in storage.', {error: domException.name});
    }
  }

  /**
   * Sets a key value pair in browser storage.
   * @param key Key with which to query storage.
   * @param value Value to be put into storage
   * @param expirationInMs Expiration time for the storage entry.
   */
  async set(key: any, value: any, expirationInMs?: number) {
    let json = JSON.stringify(value);
    let compressed = false;
    const expirationTimestamp = this.getExpireTime(expirationInMs);

    if (json.length > THRESHOLD) {
      compression = compression || await import('./compression');
      json = compression.compress(json, key);
      compressed = true;
    }

    try {
      this.storage.setItem(key, `${compressed ? 'Y' : 'N'}|${json}`);
    } catch (domException) {
      this.metrics.error('Failed to set item in storage.', {compressed, error: domException.name, key, rawSize: json.length});
    }
    this.storeExpireTime(key, expirationTimestamp);
  }

  /**
   * Removes item from storage.
   * @param key Key with which to query storage.
   */
  remove(key: string) {
    this.storage.removeItem(key);
    delete this.expirationDict[key];
    this.storeExpirationDictionary();
  }

  /**
   * Clears all values or those determined by a filter function.
   * @param filter Function to filter which keys to clear
   */
  clear(filter?: Function) {
    Object.keys(this.expirationDict).forEach(key => {
      if (!filter || filter(key)) {
        this.storage.removeItem(key);
        delete this.expirationDict[key];
      }
    });
    this.storeExpirationDictionary();
  }

  /**
   * Clears values containg an expiration time of 0, denoting that
   * they should be cleared on logout.
   */
  clearOnLogout() {
    this.clear((key: string) => this.expirationDict[key] === EXPIRE_ON_LOGOUT);
  }

  /**
   * Stores or updates an expiration time for a key.
   * @param key Key with which to query storage.
   * @param expirationTimestamp Expiration timestamp in ms.
   */
  storeExpireTime(key: string, expirationTimestamp: number) {
    this.expirationDict[key] = expirationTimestamp;
    this.storeExpirationDictionary();
  }

  /**
   * Function to check which storage entries have expired and remove them.
   */
  trackExpiration() {
    Object.keys(this.expirationDict).forEach(key => {
      let value = this.expirationDict[key];
      if (value > 0 && value < Date.now()) {
        this.metrics.event('storage expiration', `Key ${key} expired at ${value}`, {expirationTime: value, key});
        this.storage.removeItem(key);
        delete this.expirationDict[key];
      }
    });
    this.storeExpirationDictionary();
  }
}

export const storage = {
  local: new StorageInstance(localStorage),
  session: new StorageInstance(sessionStorage)
};
